/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

export type Listener<A extends unknown[] = []> = (...args: A) => void;

export interface ListenerRegistry<A extends unknown[] = []> {
  subscribe: (listener: Listener<A>) => (() => void),
  notify: (...args: A) => void,
  clear: () => void,
  size: () => number
}

/**
 * Creates a registry of listeners for a module which publishes an event.
 *
 * A module singleton which anything else observes needs the same three pieces
 * every time: a set of listeners, a `subscribe` returning the function which
 * removes the one it just added, and a `notify` which invokes them all. The
 * `subscribe` contract is the part worth writing once - a caller which is
 * handed anything other than its own disposer either leaks a listener for the
 * life of the page or removes somebody else's.
 *
 * The registry owns registration and delivery only. A publisher which does
 * more on notify - counting revisions, suppressing an unchanged value - keeps
 * that in its own `notify`, and calls this one from inside it.
 *
 * `clear` exists for test isolation, since module state outlives a test case.
 *
 * ```typescript
 *
 *   import {createListenerRegistry} from '@ion/core/src/utils/ListenerRegistry';
 *
 *   ...
 *   const listeners = createListenerRegistry<[string, number]>();
 *
 *   // ... a surface subscribes, and keeps the disposer ...
 *   const dispose = listeners.subscribe(function(name, id) {
 *     info(`${name} changed for ${id}`);
 *   });
 *
 *   // ... the module publishes ...
 *   listeners.notify('progress', 42);
 *
 *   // ... and the surface unmounts ...
 *   dispose();
 *
 * ```
 *
 * @template `A` The argument tuple every listener is invoked with. Defaults to
 *           `[]`, for an event which carries nothing.
 * @returns  `subscribe` to register one and receive its disposer, `notify` to
 *           invoke every listener, `clear` to remove them all, and `size`, the
 *           number currently registered.
 */
export const createListenerRegistry = <A extends unknown[] = []>(): ListenerRegistry<A> => {
  const listeners = new Set<Listener<A>>();

  return {
    subscribe : (listener: Listener<A>): (() => void) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    notify : (...args: A): void => {
      listeners.forEach(listener => listener(...args));
    },
    clear : (): void => {
      listeners.clear();
    },
    size : (): number => listeners.size
  };
};

export default createListenerRegistry;
